import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, role?: 'student' | 'parent') => Promise<{ error: Error | null; session: import('@supabase/supabase-js').Session | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Track auth events for analytics
        if (event === 'SIGNED_IN' && session?.user) {
          import('@/lib/posthog').then(({ trackEvent, identifyUser }) => {
            identifyUser(session.user.id, { email: session.user.email });
            trackEvent('user_signed_in');
          });

          // Record referral if the user arrived via ?ref=CODE
          import('@/services/referralService').then(
            ({ getStoredReferralCode, clearStoredReferralCode, recordReferral }) => {
              const refCode = getStoredReferralCode();
              if (refCode) {
                recordReferral(refCode, session.user.id)
                  .then((ok) => {
                    if (ok) {
                      // User-to-user referral matched a real profile
                      import('@/lib/posthog').then(({ trackEvent: track }) => {
                        track('referral_signup', { referral_code: refCode });
                      });
                    } else {
                      // No matching user profile — this is a marketing ref
                      // (e.g., "card" from QR code, "flyer", "instagram-bio")
                      import('@/lib/posthog').then(({ trackEvent: track }) => {
                        track('marketing_signup', { ref_source: refCode });
                      });
                    }
                  })
                  .catch(() => {})
                  .finally(() => clearStoredReferralCode());
              }
            },
          );
        } else if (event === 'SIGNED_OUT') {
          import('@/lib/posthog').then(({ resetUser }) => {
            resetUser();
          });
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, role?: 'student' | 'parent') => {
    // Only require email confirmation on the production domain
    // On staging/localhost, skip confirmation so testers can sign up instantly
    const isProduction = window.location.hostname === 'thescholarvision.com';
    const redirectUrl = `${window.location.origin}/auth/callback`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        ...(isProduction ? { emailRedirectTo: redirectUrl } : {}),
        data: role ? { role } : undefined,
      }
    });

    // Send welcome email on successful signup (fire-and-forget)
    if (!error) {
      supabase.functions.invoke('welcome-email', {
        body: { email, role: role || 'student' },
      }).catch((err) => console.warn('[Welcome Email] Failed to send:', err));
    }

    let session = data?.session ?? null;

    // Failsafe for non-production: if signup succeeded but no session was returned
    // (e.g., Supabase still requires confirmation), auto-sign-in immediately.
    // This guarantees staging/localhost testers can always get in.
    if (!error && !session && !isProduction) {
      console.log('[ScholarVision] Staging auto-login: signUp returned no session, attempting signIn...');
      const signInResult = await supabase.auth.signInWithPassword({ email, password });
      if (!signInResult.error) {
        session = signInResult.data.session;
      } else {
        console.warn('[ScholarVision] Staging auto-login failed:', signInResult.error.message);
      }
    }

    return { error, session };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth/reset-password`;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });

    return { error };
  };

  const signInWithMagicLink = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth/callback`;

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        resetPassword,
        signInWithMagicLink,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
